import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Smartphone, 
  Building2, 
  ShieldCheck, 
  KeyRound, 
  Webhook, 
  QrCode, 
  BookOpen, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCw, 
  Copy, 
  Check, 
  Sparkles, 
  Save, 
  FileText, 
  ArrowRightLeft, 
  Eye, 
  EyeOff, 
  Clock, 
  Send,
  Lock,
  Layers,
  CheckCircle,
  Hash,
  Download,
  AlertTriangle,
  Edit3,
  Upload,
  Image as ImageIcon,
  ZoomIn,
  X,
  ExternalLink,
  FileCheck
} from 'lucide-react';
import confetti from 'canvas-confetti';
import QRCode from 'qrcode';
import { 
  QRPaymentConfig, 
  DEFAULT_QR_CONFIG, 
  fetchQrPaymentConfig, 
  saveQrPaymentConfig, 
  subscribeQrPaymentConfig 
} from '../../lib/qrPaymentService';
import { generatePromptPayPayload, parseEmvcoPayload, ClientTlvItem } from '../../lib/emvcoQr';

export const QRPaymentConfigPanel: React.FC = () => {
  const [config, setConfig] = useState<QRPaymentConfig>(DEFAULT_QR_CONFIG);
  const [tempConfig, setTempConfig] = useState<QRPaymentConfig>(DEFAULT_QR_CONFIG);
  const [activeSubTab, setActiveSubTab] = useState<'merchant' | 'webhook' | 'simulator' | 'ledger' | 'webhooks'>('merchant');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Live Simulator state
  const [simAmount, setSimAmount] = useState<number>(1250);
  const [simOrderRef, setSimOrderRef] = useState<string>('GF-202609-8831');
  const [simQrImage, setSimQrImage] = useState<string>('');
  const [simPayload, setSimPayload] = useState<string>('');
  const [simTlv, setSimTlv] = useState<{ items: ClientTlvItem[]; isValidChecksum: boolean; calculatedCrc: string; providedCrc: string } | null>(null);
  const [simulatingPayment, setSimulatingPayment] = useState(false);
  const [simPaymentResult, setSimPaymentResult] = useState<any>(null);

  // Anti-Replay Slip Testing state
  const [testSlipText, setTestSlipText] = useState<string>('SLIP_IMAGE_TEST_DATA_HASH_778192');
  const [testSlipOrderRef, setTestSlipOrderRef] = useState<string>('GF-202609-8831');
  const [slipCheckResult, setSlipCheckResult] = useState<any>(null);
  const [verifyingSlip, setVerifyingSlip] = useState(false);

  // Ledger state
  const [ledgerData, setLedgerData] = useState<any>(null);
  const [loadingLedger, setLoadingLedger] = useState(false);

  // Tung Ngern Reference Recording state
  const [editingJournalId, setEditingJournalId] = useState<string | null>(null);
  const [tungNgernInputs, setTungNgernInputs] = useState<Record<string, string>>({});
  const [savingTungNgernId, setSavingTungNgernId] = useState<string | null>(null);

  // KTB SLIP_PAYMENT Storage & AI OCR state
  const [uploadingSlipJournalId, setUploadingSlipJournalId] = useState<string | null>(null);
  const [slipsMap, setSlipsMap] = useState<Record<string, any>>({});
  const [selectedSlipModal, setSelectedSlipModal] = useState<any | null>(null);

  // Webhook Logs state
  const [webhookLogs, setWebhookLogs] = useState<any[]>([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);

  // Sync config from service
  useEffect(() => {
    const unsub = subscribeQrPaymentConfig((updated) => {
      setConfig(updated);
      setTempConfig(updated);
    });
    return () => unsub();
  }, []);

  // Update QR code whenever simulator inputs change
  useEffect(() => {
    const updateQrSimulator = async () => {
      try {
        const activeMerchantCode = tempConfig.merchantCode || 'QV353070BI06881536X';
        const activeBillerId = tempConfig.billerId || '010753700088205';
        const activeMerchantName = tempConfig.name || 'GIFFFORYOU.COM';

        const payload = generatePromptPayPayload({
          billerId: activeBillerId,
          promptPayId: tempConfig.promptPayId || '0939678649',
          merchantCode: activeMerchantCode,
          merchantName: activeMerchantName,
          amount: simAmount,
          ref1: activeMerchantCode, // Ref 1: รหัสผู้ค้า (QV353070BI06881536X ต้องเป็นค่านี้เท่านั้น)
          ref2: simOrderRef.replace(/[^A-Za-z0-9_-]/g, ''), // Ref 2: รหัสคำสั่งซื้อ
          isDynamic: tempConfig.qrMode === 'DYNAMIC'
        });
        setSimPayload(payload);

        const img = await QRCode.toDataURL(payload, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 280,
          color: {
            dark: '#003366',
            light: '#FFFFFF'
          }
        });
        setSimQrImage(img);

        const tlv = parseEmvcoPayload(payload);
        setSimTlv(tlv);
      } catch (err) {
        console.error('Error generating simulator QR:', err);
      }
    };

    updateQrSimulator();
  }, [simAmount, simOrderRef, tempConfig.billerId, tempConfig.promptPayId, tempConfig.qrMode]);

  const copyToClipboard = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setErrorMsg('');
      setSuccessMsg('');
      const ok = await saveQrPaymentConfig(tempConfig);
      if (ok) {
        setConfig(tempConfig);
        setSuccessMsg('บันทึกการตั้งค่าการเชื่อมต่อ QR Payment และระบบ Webhook เรียบร้อยแล้ว');
        confetti({ particleCount: 35, spread: 60, origin: { y: 0.85 } });
      } else {
        setErrorMsg('ไม่สามารถบันทึกการตั้งค่าได้ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (e: any) {
      setErrorMsg(e?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateSecret = async () => {
    if (!window.confirm('คุณแน่ใจหรือไม่ว่าต้องการสร้าง Webhook Secret ใหม่? ระบบภายนอกจะต้องใช้ Secret ใหม่นี้ในการยืนยัน Signature')) return;
    try {
      const res = await fetch('/api/payment/config/generate-secret', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.webhookSecret) {
          setTempConfig(prev => ({ ...prev, webhookSecret: data.webhookSecret }));
          setSuccessMsg('สร้าง Webhook Secret ใหม่เรียบร้อยแล้ว');
        }
      }
    } catch (err) {
      setErrorMsg('สร้าง Secret ล้มเหลว');
    }
  };

  const handleSimulatePayment = async () => {
    try {
      setSimulatingPayment(true);
      setSimPaymentResult(null);
      const res = await fetch('/api/payment/simulate-pay', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderReference: simOrderRef,
          amount: simAmount,
          providerName: 'KTB_TUNG_NGERN'
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSimPaymentResult(data);
        confetti({
          particleCount: 80,
          spread: 80,
          origin: { y: 0.6 }
        });
        setSuccessMsg(`จำลองการชำระเงิน Order ${simOrderRef} ยอด ฿${simAmount.toLocaleString()} สำเร็จ! ข้อมูลส่งเข้า Ledger แล้ว`);
        // Refresh ledger if active
        if (activeSubTab === 'ledger') fetchLedger();
      } else {
        setErrorMsg(data.error || 'จำลองการชำระเงินไม่สำเร็จ');
      }
    } catch (err: any) {
      setErrorMsg('เกิดข้อผิดพลาดในการจำลองการชำระเงิน');
    } finally {
      setSimulatingPayment(false);
    }
  };

  const handleVerifySlipAntiReplay = async () => {
    try {
      setVerifyingSlip(true);
      setSlipCheckResult(null);
      const res = await fetch('/api/payment/verify-slip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slipData: testSlipText,
          orderReference: testSlipOrderRef
        })
      });
      const data = await res.json();
      setSlipCheckResult(data);
    } catch (err) {
      setErrorMsg('ตรวจสอบสลิปไม่สำเร็จ');
    } finally {
      setVerifyingSlip(false);
    }
  };

  const fetchSlips = async () => {
    try {
      const res = await fetch('/api/payment/slip/ktb');
      if (res.ok) {
        const data = await res.json();
        const map: Record<string, any> = {};
        (data.slips || []).forEach((s: any) => {
          if (s.journalId) map[s.journalId] = s;
          if (s.referenceCode) map[s.referenceCode] = s;
        });
        setSlipsMap(map);
      }
    } catch (err) {
      console.error('Failed fetching KTB slips:', err);
    }
  };

  const fetchLedger = async () => {
    try {
      setLoadingLedger(true);
      const res = await fetch('/api/payment/ledger');
      if (res.ok) {
        const data = await res.json();
        setLedgerData(data.ledger);
      }
      await fetchSlips();
    } catch (err) {
      console.error('Failed fetching ledger:', err);
    } finally {
      setLoadingLedger(false);
    }
  };

  const fetchWebhooks = async () => {
    try {
      setLoadingWebhooks(true);
      const res = await fetch('/api/payment/webhooks');
      if (res.ok) {
        const data = await res.json();
        setWebhookLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed fetching webhooks:', err);
    } finally {
      setLoadingWebhooks(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'ledger') {
      fetchLedger();
      fetchSlips();
    } else if (activeSubTab === 'webhooks') {
      fetchWebhooks();
    }
  }, [activeSubTab]);

  const handleUploadSlip = async (journalId: string, file: File) => {
    try {
      setUploadingSlipJournalId(journalId);
      setErrorMsg('');

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result as string;
          const res = await fetch('/api/payment/slip/upload-ktb-slip', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image: base64,
              journalId,
              mimeType: file.type || 'image/jpeg',
              uploadedBy: 'Admin'
            })
          });

          const data = await res.json();
          if (!res.ok || !data.success) {
            throw new Error(data.error || 'การสกัดและตรวจสอบสลิปไม่สำเร็จ');
          }

          // Auto-populate extracted reference code in the input state
          setTungNgernInputs(prev => ({ ...prev, [journalId]: data.slipRecord.referenceCode }));
          
          setSuccessMsg(data.message || `สกัดรหัสอ้างอิงถุงเงิน "${data.slipRecord.referenceCode}" สำเร็จ! ยืนยันคำสั่งซื้อเป็น "รอดำเนินการ" และบันทึกในสตอเรจ KTB_SLIP เรียบร้อย`);
          
          await fetchLedger();
          await fetchSlips();

          // Dispatch event across the app for instant multi-device reactivity
          window.dispatchEvent(new CustomEvent('order-updated', { 
            detail: { orderId: data.orderId, status: 'Processing' } 
          }));

          confetti({
            particleCount: 60,
            spread: 70,
            origin: { y: 0.6 }
          });
        } catch (err: any) {
          setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการวิเคราะห์สลิป');
        } finally {
          setUploadingSlipJournalId(null);
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการอ่านไฟล์');
      setUploadingSlipJournalId(null);
    }
  };

  const handleSaveTungNgernRef = async (journalId: string) => {
    const refCode = (tungNgernInputs[journalId] ?? '').trim();
    if (!refCode) {
      setErrorMsg('กรุณากรอกรหัสอ้างอิงถุงเงินที่ได้รับจากธนาคาร');
      return;
    }

    try {
      setSavingTungNgernId(journalId);
      setErrorMsg('');
      const res = await fetch('/api/payment/ledger/record-tungngern-ref', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          journalId,
          tungNgernRef: refCode,
          recordedBy: 'Admin'
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'บันทึกรหัสอ้างอิงไม่สำเร็จ');
      }

      setSuccessMsg(data.message || `บันทึกรหัสอ้างอิงถุงเงิน "${refCode}" สำเร็จ! ยืนยันคำสั่งซื้อเป็น "รอดำเนินการ" เรียบร้อยแล้ว`);
      setEditingJournalId(null);
      await fetchLedger();

      // Dispatch real-time global event for instant UI update
      window.dispatchEvent(new CustomEvent('order-updated', { 
        detail: { orderId: data.orderId, status: 'Processing' } 
      }));

      // Confetti celebration
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 }
      });
    } catch (err: any) {
      setErrorMsg(err?.message || 'เกิดข้อผิดพลาดในการบันทึกรหัสอ้างอิงถุงเงิน');
    } finally {
      setSavingTungNgernId(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Alert Messages */}
      {successMsg && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-emerald-800 text-xs font-semibold shadow-xs animate-in fade-in">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{successMsg}</span>
          </div>
          <button onClick={() => setSuccessMsg('')} className="text-emerald-700 hover:text-emerald-900 font-bold">✕</button>
        </div>
      )}

      {errorMsg && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-center justify-between text-rose-800 text-xs font-semibold shadow-xs animate-in fade-in">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>{errorMsg}</span>
          </div>
          <button onClick={() => setErrorMsg('')} className="text-rose-700 hover:text-rose-900 font-bold">✕</button>
        </div>
      )}

      {/* Main Architectural Header Card */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-5 sm:p-6 shadow-md border border-slate-700/50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 border border-teal-400/30 text-teal-300 text-[11px] font-bold tracking-wide uppercase mb-2">
              <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              EMVCo ISO/IEC 18004 Engine • BOT PromptPay 2.0
            </div>
            <h2 className="text-lg sm:text-xl font-black text-white tracking-tight flex items-center gap-2.5">
              <Smartphone className="w-6 h-6 text-teal-400" />
              เชื่อมต่อระบบ คิวอาร์เพย์เม้นท์ (QR Payment Architecture)
            </h2>
            <p className="text-xs text-slate-300 mt-1 max-w-2xl leading-relaxed">
              สถาปัตยกรรมระบบรับชำระเงินพร้อมเพย์ความเร็วสูง รองรับ Dynamic EMVCo QR Code, ระบบตรวจสอบสลิปป้องกันการใช้ซ้ำ (Anti-Replay Slip SHA-256), Webhook Idempotency และสมุดบัญชีแยกประเภทคู่ (Double-Entry Financial Ledger)
            </p>
          </div>

          {/* Save / Status Header Action */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm shadow-teal-500/20 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
            >
              <Save className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
              <span>{saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่าทั้งหมด'}</span>
            </button>
          </div>
        </div>

        {/* Live Architecture Metric Badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-slate-700/60">
          <div className="bg-slate-800/60 backdrop-blur-xs border border-slate-700/80 rounded-xl p-2.5">
            <div className="text-[10px] text-slate-400 font-bold uppercase">Biller / PromptPay ID</div>
            <div className="text-xs font-black text-teal-300 font-mono mt-0.5 truncate">
              {tempConfig.billerId || tempConfig.promptPayId}
            </div>
          </div>

          <div className="bg-slate-800/60 backdrop-blur-xs border border-slate-700/80 rounded-xl p-2.5">
            <div className="text-[10px] text-slate-400 font-bold uppercase">QR Generation Mode</div>
            <div className="text-xs font-black text-emerald-400 mt-0.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              {tempConfig.qrMode === 'DYNAMIC' ? 'Dynamic (ระบุยอดอัตโนมัติ)' : 'Static (ยอดคงที่)'}
            </div>
          </div>

          <div className="bg-slate-800/60 backdrop-blur-xs border border-slate-700/80 rounded-xl p-2.5">
            <div className="text-[10px] text-slate-400 font-bold uppercase">Webhook Ingestion</div>
            <div className="text-xs font-black text-indigo-300 mt-0.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
              HMAC-SHA256 Active
            </div>
          </div>

          <div className="bg-slate-800/60 backdrop-blur-xs border border-slate-700/80 rounded-xl p-2.5">
            <div className="text-[10px] text-slate-400 font-bold uppercase">Ledger Equilibrium</div>
            <div className="text-xs font-black text-amber-300 mt-0.5 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5 text-amber-400" />
              Debit == Credit 100%
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 bg-slate-100 p-1.5 rounded-xl border border-slate-200/80 text-xs font-bold">
        {[
          { id: 'merchant', label: '1. ข้อมูลผู้ค้า & Biller ID', icon: Building2 },
          { id: 'webhook', label: '2. ระบบ Webhook & ความปลอดภัย', icon: Webhook },
          { id: 'simulator', label: '3. ทดสอบ Dynamic QR & TLV', icon: QrCode, highlight: true },
          { id: 'ledger', label: '4. สมุดบัญชีแยกประเภทคู่ (Ledger)', icon: BookOpen },
          { id: 'webhooks', label: '5. บันทึก Webhook & Anti-Replay', icon: FileText },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeSubTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id as any)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg transition-all cursor-pointer select-none ${
                isActive
                  ? 'bg-white text-teal-800 font-black shadow-xs border border-teal-200/80'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-teal-600' : 'text-slate-400'}`} />
              <span>{tab.label}</span>
              {tab.highlight && (
                <span className="text-[9px] font-black px-1.5 py-0.2 rounded-full bg-pink-100 text-pink-700">
                  Sandbox
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ========================================================= */}
      {/* SUB-TAB 1: MERCHANT & BILLER CREDENTIALS */}
      {/* ========================================================= */}
      {activeSubTab === 'merchant' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 sm:p-6 space-y-6 animate-in fade-in">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-teal-600" />
                ตั้งค่าข้อมูลผู้ค้าและช่องทางรับชำระเงิน (Merchant & Biller Profile)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                กำหนดรหัสร้านค้า Biller ID (KTB / PromptPay Bill Payment) สำหรับสร้าง Dynamic EMVCo QR
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <span className="text-xs font-bold text-slate-700">สถานะเปิดรับชำระ:</span>
              <input
                type="checkbox"
                checked={tempConfig.isActive}
                onChange={(e) => setTempConfig({ ...tempConfig, isActive: e.target.checked })}
                className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500 cursor-pointer"
              />
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tempConfig.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                {tempConfig.isActive ? 'เปิดใช้งาน (Active)' : 'ปิดใช้งาน'}
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Merchant Code */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-700">
                  รหัสผู้ค้า (Merchant Code) * REF1: QV353070BI06881536X <span className="text-rose-500">*</span>
                </label>
                {tempConfig.merchantCode !== 'QV353070BI06881536X' && (
                  <button
                    type="button"
                    onClick={() => setTempConfig({ ...tempConfig, merchantCode: 'QV353070BI06881536X' })}
                    className="text-[10.5px] font-bold text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  >
                    ใช้รหัสมาตรฐาน QV353070BI06881536X
                  </button>
                )}
              </div>
              <input
                type="text"
                value={tempConfig.merchantCode}
                onChange={(e) => setTempConfig({ ...tempConfig, merchantCode: e.target.value.trim().toUpperCase() })}
                placeholder="QV353070BI06881536X"
                className={`w-full text-xs font-mono font-bold px-3 py-2 border rounded-xl focus:outline-none transition-colors ${
                  tempConfig.merchantCode === 'QV353070BI06881536X'
                    ? 'border-slate-300 text-slate-900 bg-slate-50 focus:bg-white focus:border-teal-500'
                    : 'border-amber-400 text-amber-900 bg-amber-50/50 focus:border-amber-500'
                }`}
              />
              {tempConfig.merchantCode !== 'QV353070BI06881536X' ? (
                <span className="text-[11px] text-amber-600 font-medium mt-1 block">
                  ⚠️ รหัสร้านค้าปัจจุบัน ({tempConfig.merchantCode || 'ว่าง'}) ต่างจากรหัสทางการของระบบ (QV353070BI06881536X)
                </span>
              ) : (
                <span className="text-[11px] text-emerald-600 font-medium mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> รหัสร้านค้าถูกต้องตรงตามมาตรฐานทางการ (QV353070BI06881536X)
                </span>
              )}
            </div>

            {/* Merchant Name */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                ชื่อร้านค้า / ผู้รับเงิน (Merchant Name) <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={tempConfig.name}
                onChange={(e) => setTempConfig({ ...tempConfig, name: e.target.value })}
                placeholder="GIFFFORYOU.COM"
                className="w-full text-xs font-bold text-slate-900 px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:border-teal-500 bg-slate-50 focus:bg-white transition-colors"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">ชื่อที่จะแสดงบนหน้าสแกน QR Code และสลิปการโอนเงิน</span>
            </div>

            {/* Biller ID */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-700">
                  รหัส Biller ID (KTB ถุงเงิน / Bill Payment Tag 30)
                </label>
                {tempConfig.billerId !== '010753700088205' && (
                  <button
                    type="button"
                    onClick={() => setTempConfig({ ...tempConfig, billerId: '010753700088205' })}
                    className="text-[10.5px] font-bold text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  >
                    ใช้รหัสมาตรฐาน KTB 010753700088205
                  </button>
                )}
              </div>
              <input
                type="text"
                value={tempConfig.billerId}
                onChange={(e) => setTempConfig({ ...tempConfig, billerId: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="010753700088205"
                className={`w-full text-xs font-mono font-bold px-3 py-2 border rounded-xl focus:outline-none transition-colors ${
                  tempConfig.billerId === '010753700088205'
                    ? 'border-slate-300 text-slate-900 bg-slate-50 focus:bg-white focus:border-teal-500'
                    : 'border-amber-400 text-amber-900 bg-amber-50/50 focus:border-amber-500'
                }`}
              />
              {tempConfig.billerId === '010753700088205' ? (
                <span className="text-[11px] text-emerald-600 font-medium mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> รหัส Biller ID KTB ถุงเงินถูกต้อง (15 หลัก: 010753700088205)
                </span>
              ) : (
                <span className="text-[11px] text-slate-400 mt-1 block">รหัส Biller ID 13-15 หลัก สำหรับระบบ Bill Payment ธนาคารกรุงไทย หรือ ธนาคารพาณิชย์</span>
              )}
            </div>

            {/* PromptPay Mobile / Tax ID */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                เบอร์โทรศัพท์พร้อมเพย์ หรือ เลขนิติบุคคล (PromptPay ID Tag 29)
              </label>
              <input
                type="text"
                value={tempConfig.promptPayId}
                onChange={(e) => setTempConfig({ ...tempConfig, promptPayId: e.target.value })}
                placeholder="0939678649"
                className="w-full text-xs font-mono font-bold text-slate-900 px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:border-teal-500 bg-slate-50 focus:bg-white transition-colors"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">ใช้เมื่อต้องการรับชำระผ่าน PromptPay ทั่วไป (แปลงเป็น 0066... อัตโนมัติ)</span>
            </div>

            {/* QR Mode */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                รูปแบบการสร้าง QR Code (QR Generation Mode)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`p-3 rounded-xl border cursor-pointer select-none flex items-center gap-2.5 ${
                  tempConfig.qrMode === 'DYNAMIC' ? 'border-teal-500 bg-teal-50/50 font-bold text-teal-900' : 'border-slate-200 bg-white text-slate-600'
                }`}>
                  <input
                    type="radio"
                    name="qrMode"
                    checked={tempConfig.qrMode === 'DYNAMIC'}
                    onChange={() => setTempConfig({ ...tempConfig, qrMode: 'DYNAMIC' })}
                    className="text-teal-600 focus:ring-teal-500 cursor-pointer"
                  />
                  <div>
                    <div className="text-xs font-bold">Dynamic QR (แนะนำ)</div>
                    <div className="text-[10px] text-slate-500">ระบุยอดและเลขออเดอร์อัตโนมัติ</div>
                  </div>
                </label>

                <label className={`p-3 rounded-xl border cursor-pointer select-none flex items-center gap-2.5 ${
                  tempConfig.qrMode === 'STATIC' ? 'border-teal-500 bg-teal-50/50 font-bold text-teal-900' : 'border-slate-200 bg-white text-slate-600'
                }`}>
                  <input
                    type="radio"
                    name="qrMode"
                    checked={tempConfig.qrMode === 'STATIC'}
                    onChange={() => setTempConfig({ ...tempConfig, qrMode: 'STATIC' })}
                    className="text-teal-600 focus:ring-teal-500 cursor-pointer"
                  />
                  <div>
                    <div className="text-xs font-bold">Static QR</div>
                    <div className="text-[10px] text-slate-500">ลูกค้ากรอกยอดเอง</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Expiry Minutes */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                อายุของ QR Code ก่อนหมดอายุ (QR Expiration Timeout)
              </label>
              <select
                value={tempConfig.qrExpiryMinutes}
                onChange={(e) => setTempConfig({ ...tempConfig, qrExpiryMinutes: Number(e.target.value) })}
                className="w-full text-xs font-bold text-slate-900 px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:border-teal-500 bg-slate-50 focus:bg-white transition-colors cursor-pointer"
              >
                <option value={5}>5 นาที (Fast Expiration)</option>
                <option value={10}>10 นาที</option>
                <option value={15}>15 นาที (แนะนำสำหรับ E-Commerce)</option>
                <option value={30}>30 นาที</option>
                <option value={60}>60 นาที (1 ชั่วโมง)</option>
              </select>
              <span className="text-[11px] text-slate-400 mt-1 block">เมื่อหมดเวลาระบบจะยกเลิก Payment Session อัตโนมัติ</span>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end">
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-xl shadow-2xs active:scale-95 transition-all cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>บันทึกข้อมูลผู้ค้า (Save Merchant Profile)</span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* SUB-TAB 2: WEBHOOK SECURITY & ENDPOINTS */}
      {/* ========================================================= */}
      {activeSubTab === 'webhook' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 sm:p-6 space-y-6 animate-in fade-in">
          <div className="border-b border-slate-100 pb-4">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Webhook className="w-4 h-4 text-indigo-600" />
              การเชื่อมต่อ Webhook และความปลอดภัย (Webhook Ingestion & HMAC-SHA256)
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              รับข้อมูลการแจ้งเตือนการชำระเงินจากธนาคารแบบ Real-time พร้อมการตรวจสอบ Signature เพื่อความปลอดภัยสูงสุด
            </p>
          </div>

          <div className="space-y-4">
            {/* Webhook Endpoint URL */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                Webhook Callback URL (URL สำหรับนำไปผูกที่ระบบธนาคาร / Gateway)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={window.location.origin + tempConfig.webhookUrl}
                  className="flex-1 text-xs font-mono font-bold text-indigo-900 px-3 py-2.5 bg-indigo-50/60 border border-indigo-200 rounded-xl"
                />
                <button
                  onClick={() => copyToClipboard(window.location.origin + tempConfig.webhookUrl, 'webhookUrl')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer shrink-0"
                >
                  {copiedKey === 'webhookUrl' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copiedKey === 'webhookUrl' ? 'คัดลอกแล้ว!' : 'คัดลอก URL'}</span>
                </button>
              </div>
              <span className="text-[11px] text-slate-400 mt-1 block">รองรับ Method POST พร้อม Content-Type: application/json</span>
            </div>

            {/* Webhook Secret Key */}
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1">
                Webhook Secret Key (HMAC-SHA256 Signature Verification)
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    readOnly
                    value={tempConfig.webhookSecret}
                    className="w-full text-xs font-mono font-bold text-slate-900 px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-xl pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  onClick={() => copyToClipboard(tempConfig.webhookSecret, 'secret')}
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors cursor-pointer shrink-0 border border-slate-200"
                >
                  {copiedKey === 'secret' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  <span>{copiedKey === 'secret' ? 'คัดลอกแล้ว' : 'คัดลอก Key'}</span>
                </button>
                <button
                  onClick={handleRegenerateSecret}
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold rounded-xl transition-colors cursor-pointer shrink-0 border border-rose-200"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>สร้างคีย์ใหม่</span>
                </button>
              </div>
              <span className="text-[11px] text-slate-400 mt-1 block">ใช้สำหรับตรวจสอบความถูกต้องของลายเซ็นดิจิทัล `x-signature` ป้องกันการปลอมแปลง Webhook</span>
            </div>

            {/* Zero-Trust Security Features */}
            <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2.5">
              <div className="text-xs font-bold text-slate-800 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                มาตรฐานความปลอดภัยที่เปิดใช้งานอัตโนมัติ (Zero-Trust Security Patterns)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span><strong>Idempotency Enforcement:</strong> ป้องกันการประมวลผลออเดอร์ซ้ำซ้อน 100%</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span><strong>Anti-Replay Slip SHA-256:</strong> ตรวจจับและบล็อกสลิปเก่าที่นำมาใช้ใหม่</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span><strong>Double-Entry Ledger:</strong> บันทึกบัญชีแยกประเภทคู่ตรวจสอบยอดเงินจริง</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span><strong>Dead-Letter Queue (DLQ):</strong> กักเก็บและติดตาม Webhook ที่มีข้อผิดพลาด</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* SUB-TAB 3: LIVE SIMULATOR & TLV INSPECTOR */}
      {/* ========================================================= */}
      {activeSubTab === 'simulator' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 animate-in fade-in">
          {/* Left: Simulator Controls & Dynamic QR */}
          <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-4 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-teal-600" />
                  จำลองสร้าง Dynamic PromptPay QR
                </h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">
                  Live Preview
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    ยอดเงินจำลอง (Amount in THB)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">฿</span>
                    <input
                      type="number"
                      step="0.25"
                      min="1"
                      value={simAmount}
                      onChange={(e) => setSimAmount(Math.max(1, parseFloat(e.target.value) || 0))}
                      className="w-full text-xs font-bold text-slate-900 pl-7 pr-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:border-teal-500 bg-slate-50 focus:bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    เลขอ้างอิงคำสั่งซื้อ (Order Reference / Ref 1)
                  </label>
                  <input
                    type="text"
                    value={simOrderRef}
                    onChange={(e) => setSimOrderRef(e.target.value)}
                    className="w-full text-xs font-mono font-bold text-slate-900 px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:border-teal-500 bg-slate-50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Rendered PromptPay Card */}
              <div className="mt-4 p-4 rounded-2xl bg-gradient-to-b from-[#003366] to-[#002244] text-white flex flex-col items-center shadow-md">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-black text-[#003366]">
                    ฿
                  </div>
                  <div className="text-xs font-black tracking-wider uppercase">
                    Thai QR Payment / พร้อมเพย์
                  </div>
                </div>

                {/* QR Code Container */}
                <div className="bg-white p-3 rounded-xl shadow-inner my-2">
                  {simQrImage ? (
                    <img src={simQrImage} alt="PromptPay QR" className="w-48 h-48 object-contain" />
                  ) : (
                    <div className="w-48 h-48 flex items-center justify-center text-xs text-slate-400">กำลังสร้าง QR...</div>
                  )}
                </div>

                <div className="text-center mt-2 space-y-1">
                  <div className="text-[11px] text-teal-200 font-medium">ยอดชำระเงิน (Dynamic Thai QR)</div>
                  <div className="text-2xl font-black text-white tracking-tight">
                    ฿{simAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-[10.5px] font-mono text-slate-200 bg-black/20 rounded-lg p-2 mt-1 space-y-0.5 text-left border border-white/10">
                    <div className="truncate"><span className="text-slate-400">รหัสผู้ค้า (Ref 1):</span> <span className="font-bold text-amber-300">{tempConfig.merchantCode || 'QV353070BI06881536X'}</span></div>
                    <div className="truncate"><span className="text-slate-400">รหัสคำสั่งซื้อ (Ref 2):</span> <span className="font-bold text-amber-300">{simOrderRef}</span></div>
                    <div className="truncate"><span className="text-slate-400">Biller ID (Tag 30):</span> <span className="font-bold text-teal-300">{tempConfig.billerId || '010753700088205'}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Simulate Customer Payment Action Button */}
            <div className="pt-3 border-t border-slate-100">
              <button
                onClick={handleSimulatePayment}
                disabled={simulatingPayment}
                className="w-full py-2.5 px-4 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Sparkles className={`w-4 h-4 ${simulatingPayment ? 'animate-spin' : ''}`} />
                <span>{simulatingPayment ? 'กำลังประมวลผล Webhook...' : '⚡ จำลองลูกค้าสแกนจ่ายสำเร็จ (Simulate Instant Pay)'}</span>
              </button>
            </div>
          </div>

          {/* Right: EMVCo TLV Inspector & Raw Payload */}
          <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-4 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-3">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-indigo-600" />
                  แจกแจงโครงสร้างมาตรฐาน EMVCo TLV (Tag-Length-Value)
                </h3>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                  simTlv?.isValidChecksum ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                }`}>
                  <CheckCircle className="w-3 h-3" />
                  CRC16: {simTlv?.providedCrc || 'PASS'}
                </span>
              </div>

              {/* Raw Payload string */}
              <div className="p-3 bg-slate-900 rounded-xl text-slate-200 font-mono text-[11px] break-all leading-relaxed relative">
                <div className="text-[10px] font-bold text-slate-400 uppercase mb-1 flex items-center justify-between">
                  <span>Raw EMVCo Payload String:</span>
                  <button
                    onClick={() => copyToClipboard(simPayload, 'rawPayload')}
                    className="text-teal-400 hover:text-teal-300 font-sans text-[10px] font-bold inline-flex items-center gap-1 cursor-pointer"
                  >
                    {copiedKey === 'rawPayload' ? 'คัดลอกแล้ว' : 'คัดลอก Payload'}
                  </button>
                </div>
                {simPayload}
              </div>

              {/* TLV Items Table */}
              <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold sticky top-0">
                    <tr>
                      <th className="p-2.5 font-mono">Tag</th>
                      <th className="p-2.5">Len</th>
                      <th className="p-2.5">ความหมาย (Field Name)</th>
                      <th className="p-2.5 font-mono">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {simTlv?.items.map((item, idx) => (
                      <React.Fragment key={idx}>
                        <tr className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-2.5 font-mono font-bold text-teal-700">{item.tag}</td>
                          <td className="p-2.5 font-mono text-slate-500">{item.length}</td>
                          <td className="p-2.5 font-bold text-slate-900">
                            <div>{item.name}</div>
                            <div className="text-[10px] text-slate-400 font-normal">{item.description}</div>
                          </td>
                          <td className="p-2.5 font-mono text-indigo-700 font-semibold break-all">{item.value}</td>
                        </tr>
                        {/* Render Subtags if any */}
                        {item.children && item.children.map((sub, sIdx) => (
                          <tr key={`${idx}-${sIdx}`} className="bg-indigo-50/40 hover:bg-indigo-50/70 text-[11px]">
                            <td className="p-2 pl-6 font-mono text-indigo-600 font-bold">↳ {sub.tag}</td>
                            <td className="p-2 font-mono text-slate-400">{sub.length}</td>
                            <td className="p-2 text-indigo-950 font-medium">
                              <div>{sub.name}</div>
                              <div className="text-[10px] text-slate-400">{sub.description}</div>
                            </td>
                            <td className="p-2 font-mono text-indigo-900 break-all">{sub.value}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Simulation Result Box */}
            {simPaymentResult && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs space-y-1 animate-in fade-in">
                <div className="font-bold text-emerald-900 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>ผลการจำลองชำระเงินสำเร็จ (Payment Intent Succeeded)</span>
                </div>
                <div className="text-emerald-700 font-mono text-[11px]">
                  Transaction ID: {simPaymentResult.result?.transactionId} • Double-Entry Journal Posted ✅
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* SUB-TAB 4: DOUBLE-ENTRY FINANCIAL LEDGER */}
      {/* ========================================================= */}
      {activeSubTab === 'ledger' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 sm:p-6 space-y-6 animate-in fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-teal-600" />
                สมุดบัญชีแยกประเภทคู่และกระทบยอดเงิน (Double-Entry Financial Ledger)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                ตรวจสอบสมดุลทางบัญชี (Debit = Credit) และรายการบันทึกเงินสดรับจริงแบบเรียลไทม์
              </p>
            </div>

            <button
              onClick={fetchLedger}
              disabled={loadingLedger}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors cursor-pointer w-fit"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-teal-600 ${loadingLedger ? 'animate-spin' : ''}`} />
              <span>รีเฟรชยอดบัญชี</span>
            </button>
          </div>

          {/* Ledger KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ledgerData?.accounts?.map((acc: any) => (
              <div key={acc.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/60 shadow-2xs">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 truncate">
                  {acc.name}
                </div>
                <div className="text-xl font-black text-slate-900 tracking-tight font-mono">
                  ฿{Number(acc.balance || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] font-mono text-slate-400 mt-1">
                  Type: {acc.accountType}
                </div>
              </div>
            ))}
          </div>

          {/* Balanced Status Banner */}
          <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between text-xs text-emerald-900 font-bold">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>สถานะสมดุลบัญชี: เดบิตรวม (Total Debit) ฿{Number(ledgerData?.totalDebit || 0).toLocaleString()} = เครดิตรวม (Total Credit) ฿{Number(ledgerData?.totalCredit || 0).toLocaleString()}</span>
            </div>
            <span className="text-[10px] bg-emerald-200/80 text-emerald-900 px-2.5 py-0.5 rounded-full uppercase">
              Balanced 100%
            </span>
          </div>

          {/* Journal Entries Table */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2.5">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <span>รายการลงบัญชีรายวันล่าสุด (Recent Journal Entries)</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-800 border border-teal-200">
                  KTB ถุงเงิน Sync
                </span>
              </h4>
              <p className="text-[11px] text-slate-500">
                กรอกหมายเลขอ้างอิงจากแอปถุงเงินเพื่อยืนยันคำสั่งซื้อเป็น "รอดำเนินการ" ทันที
              </p>
            </div>
            
            <div className="border border-slate-200 rounded-xl overflow-x-auto shadow-2xs">
              <table className="w-full text-left text-xs min-w-[940px]">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                  <tr>
                    <th className="p-3 font-mono">Journal ID</th>
                    <th className="p-3">วันที่-เวลา</th>
                    <th className="p-3">คำอธิบายรายการ</th>
                    <th className="p-3">บัญชีเดบิต (Debit)</th>
                    <th className="p-3">บัญชีเครดิต (Credit)</th>
                    <th className="p-3 text-right">จำนวนเงิน (฿)</th>
                    <th className="p-3 min-w-[190px] bg-amber-50/80 text-amber-900 border-l border-amber-200 font-black">
                      <div className="flex items-center gap-1.5">
                        <FileCheck className="w-3.5 h-3.5 text-amber-600" />
                        <span>SLIP_PAYMENT</span>
                      </div>
                    </th>
                    <th className="p-3 min-w-[210px] bg-teal-50/70 text-teal-900 border-l border-teal-100">
                      รหัสอ้างอิงถุงเงิน
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {ledgerData?.recentEntries?.length > 0 ? (
                    ledgerData.recentEntries.map((entry: any) => {
                      const matchedSlip = slipsMap[entry.id] || (entry.tungNgernRef && slipsMap[entry.tungNgernRef]);
                      return (
                        <tr key={entry.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 font-mono text-teal-700 font-bold whitespace-nowrap">{entry.id}</td>
                          <td className="p-3 text-slate-500 whitespace-nowrap">
                            {new Date(entry.createdAt).toLocaleString('th-TH')}
                          </td>
                          <td className="p-3 font-medium text-slate-900">{entry.description}</td>
                          <td className="p-3 font-mono text-emerald-700 font-semibold">{entry.debitAccountId}</td>
                          <td className="p-3 font-mono text-indigo-700 font-semibold">{entry.creditAccountId}</td>
                          <td className="p-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                            ฿{Number(entry.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                          </td>
                          
                          {/* COLUMN: SLIP_PAYMENT */}
                          <td className="p-3 border-l border-amber-100 bg-amber-50/30">
                            {matchedSlip ? (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSelectedSlipModal(matchedSlip)}
                                  className="group relative w-10 h-10 rounded-lg border border-teal-300 overflow-hidden bg-slate-100 shadow-2xs hover:ring-2 hover:ring-teal-500 transition-all shrink-0 cursor-pointer"
                                  title="คลิกเพื่อดูสลิป KTB_SLIP แบบเต็มและผลการตรวจสอบความถูกต้อง"
                                >
                                  <img
                                    src={matchedSlip.slipImageUrl}
                                    alt="KTB Slip"
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                    onError={(e: any) => {
                                      e.currentTarget.src = 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=100&auto=format&fit=crop&q=60';
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <ZoomIn className="w-3.5 h-3.5 text-white" />
                                  </div>
                                </button>
                                
                                <div className="flex flex-col text-left">
                                  <div className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                                    <span>KTB Verified</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedSlipModal(matchedSlip)}
                                    className="text-[10px] font-mono text-teal-700 hover:text-teal-900 underline text-left truncate max-w-[100px] cursor-pointer"
                                    title="ดูสลิปในสตอเรจ KTB_SLIP"
                                  >
                                    Ref: {matchedSlip.referenceCode}
                                  </button>
                                </div>

                                <label 
                                  className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded cursor-pointer transition-colors shrink-0"
                                  title="อัปโหลดสลิปใหม่ทับ"
                                >
                                  <Upload className="w-3.5 h-3.5" />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleUploadSlip(entry.id, file);
                                    }}
                                  />
                                </label>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-teal-400 bg-teal-50 hover:bg-teal-100/90 text-teal-900 text-xs font-bold cursor-pointer transition-all shadow-2xs ${
                                  uploadingSlipJournalId === entry.id ? 'opacity-60 pointer-events-none' : ''
                                }`}>
                                  {uploadingSlipJournalId === entry.id ? (
                                    <>
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin text-teal-700" />
                                      <span className="text-[11px]">AI สกัดสลิป KTB...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-3.5 h-3.5 text-teal-700 shrink-0" />
                                      <span className="text-[11px] whitespace-nowrap">อัปโหลดสลิปถุงเงิน</span>
                                    </>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleUploadSlip(entry.id, file);
                                    }}
                                  />
                                </label>
                              </div>
                            )}
                          </td>

                          {/* COLUMN: รหัสอ้างอิงถุงเงิน */}
                          <td className="p-3 border-l border-slate-100 bg-slate-50/30">
                            {entry.tungNgernRef && editingJournalId !== entry.id ? (
                              <div className="flex items-center gap-2">
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-900 font-mono font-bold text-xs shadow-2xs">
                                  <CheckCircle className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                                  <span>{entry.tungNgernRef}</span>
                                </div>
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 whitespace-nowrap">
                                  รอดำเนินการ
                                </span>
                                <button
                                  onClick={() => {
                                    setEditingJournalId(entry.id);
                                    setTungNgernInputs(prev => ({ ...prev, [entry.id]: entry.tungNgernRef || '' }));
                                  }}
                                  className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded transition-colors"
                                  title="แก้ไขรหัสอ้างอิง"
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  placeholder="ระบุเลข Ref ธนาคาร..."
                                  value={tungNgernInputs[entry.id] ?? (entry.tungNgernRef || '')}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setTungNgernInputs(prev => ({ ...prev, [entry.id]: val }));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSaveTungNgernRef(entry.id);
                                    }
                                  }}
                                  className="w-36 sm:w-40 px-2.5 py-1 text-xs font-mono rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                />
                                <button
                                  onClick={() => handleSaveTungNgernRef(entry.id)}
                                  disabled={savingTungNgernId === entry.id}
                                  className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-lg shadow-2xs transition-all shrink-0 cursor-pointer"
                                  title="บันทึกและยืนยันคำสั่งซื้อเป็น รอดำเนินการ"
                                >
                                  {savingTungNgernId === entry.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Save className="w-3 h-3 mr-1" />
                                      <span>บันทึก</span>
                                    </>
                                  )}
                                </button>
                                {editingJournalId === entry.id && (
                                  <button
                                    onClick={() => setEditingJournalId(null)}
                                    className="p-1 text-slate-400 hover:text-slate-600 text-[11px]"
                                  >
                                    ยกเลิก
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-slate-400 font-medium">
                        ยังไม่มีรายการลงบัญชี (กดปุ่มจำลองชำระเงินเพื่อสร้างรายการ)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* SUB-TAB 5: WEBHOOK AUDIT LOGS & ANTI-REPLAY ENGINE */}
      {/* ========================================================= */}
      {activeSubTab === 'webhooks' && (
        <div className="space-y-5 animate-in fade-in">
          {/* Anti-Replay Slip Testing Section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 sm:p-6 space-y-4">
            <div className="border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                ระบบตรวจสอบสลิปป้องกันการนำสลิปเก่ามาใช้ซ้ำ (Anti-Replay Slip SHA-256)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                คำนวณและตรวจสอบ SHA-256 Checksum ของรูปภาพสลิปเพื่อป้องกันการทุจริตแนบสลิปเดิม
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  ข้อมูลรูปสลิป / รหัสอ้างอิงสลิป
                </label>
                <input
                  type="text"
                  value={testSlipText}
                  onChange={(e) => setTestSlipText(e.target.value)}
                  className="w-full text-xs font-mono text-slate-900 px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:border-teal-500 bg-slate-50 focus:bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  เลขอ้างอิงคำสั่งซื้อ
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={testSlipOrderRef}
                    onChange={(e) => setTestSlipOrderRef(e.target.value)}
                    className="flex-1 text-xs font-mono text-slate-900 px-3 py-2 border border-slate-300 rounded-xl focus:outline-none focus:border-teal-500 bg-slate-50 focus:bg-white"
                  />
                  <button
                    onClick={handleVerifySlipAntiReplay}
                    disabled={verifyingSlip}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer shrink-0"
                  >
                    {verifyingSlip ? 'กำลังตรวจ...' : 'ตรวจสอบสลิป'}
                  </button>
                </div>
              </div>
            </div>

            {slipCheckResult && (
              <div className={`p-3.5 rounded-xl border text-xs font-medium space-y-1 ${
                slipCheckResult.isDuplicate
                  ? 'bg-rose-50 border-rose-200 text-rose-900'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-900'
              }`}>
                <div className="font-bold flex items-center gap-1.5">
                  {slipCheckResult.isDuplicate ? <AlertTriangle className="w-4 h-4 text-rose-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  <span>{slipCheckResult.message}</span>
                </div>
                <div className="font-mono text-[11px] text-slate-600 break-all">
                  SHA-256 Hash: {slipCheckResult.hash}
                </div>
              </div>
            )}
          </div>

          {/* Webhook Logs Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                ประวัติการรับ Webhook จากธนาคารและ Gateway (Webhook Ingestion Logs)
              </h3>
              <button
                onClick={fetchWebhooks}
                disabled={loadingWebhooks}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors cursor-pointer"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-indigo-600 ${loadingWebhooks ? 'animate-spin' : ''}`} />
                <span>รีเฟรชประวัติ</span>
              </button>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                  <tr>
                    <th className="p-3 font-mono">Provider</th>
                    <th className="p-3 font-mono">Event ID</th>
                    <th className="p-3">ประเภท Event</th>
                    <th className="p-3">สถานะประมวลผล</th>
                    <th className="p-3">เวลาที่ได้รับ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {webhookLogs.length > 0 ? (
                    webhookLogs.map((log: any) => (
                      <tr key={log.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-3 font-mono font-bold text-teal-700">{log.providerName}</td>
                        <td className="p-3 font-mono text-slate-600">{log.eventId}</td>
                        <td className="p-3 font-bold text-slate-900">{log.eventType}</td>
                        <td className="p-3">
                          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                            log.processedStatus === 'PROCESSED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : log.processedStatus === 'PENDING'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}>
                            {log.processedStatus}
                          </span>
                        </td>
                        <td className="p-3 text-slate-500 whitespace-nowrap">
                          {new Date(log.receivedAt).toLocaleString('th-TH')}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-slate-400 font-medium">
                        ยังไม่มีประวัติ Webhook (จะปรากฏเมื่อธนาคารส่งการแจ้งเตือนเข้ามา)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: KTB TUNG-NGERN SLIP VIEWER & VERIFICATION DETAILS */}
      {/* ========================================================= */}
      {selectedSlipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200 animate-in zoom-in-95 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center shadow-2xs font-bold text-xs">
                  KTB
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 flex items-center gap-2">
                    <span>รายละเอียดสลิป KTB_SLIP</span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      {selectedSlipModal.verificationStatus || 'VERIFIED'}
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-500 font-mono">
                    Primary Key (รหัสอ้างอิง): {selectedSlipModal.referenceCode}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedSlipModal(null)}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 overflow-y-auto space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Left: Slip Image */}
                <div className="flex flex-col items-center justify-center p-3 bg-slate-100/70 border border-slate-200 rounded-xl">
                  <img
                    src={selectedSlipModal.slipImageUrl}
                    alt="KTB Slip High Res"
                    className="max-h-72 w-auto object-contain rounded-lg shadow-sm border border-slate-200"
                    onError={(e: any) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&auto=format&fit=crop&q=80';
                    }}
                  />
                  <div className="mt-2 text-center text-[10px] text-slate-400 font-mono break-all">
                    Storage: public/storage/KTB_SLIP/
                  </div>
                </div>

                {/* Right: Extracted Data & Validations */}
                <div className="space-y-3.5 text-xs">
                  <div className="p-3 bg-teal-50/70 rounded-xl border border-teal-200 space-y-1.5">
                    <div className="text-[10px] font-bold text-teal-800 uppercase tracking-wider">
                      รหัสอ้างอิงถุงเงิน (Primary Key)
                    </div>
                    <div className="text-base font-black font-mono text-teal-950">
                      {selectedSlipModal.referenceCode}
                    </div>
                  </div>

                  <div className="space-y-2 border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">หมายเลขคำสั่งซื้อ:</span>
                      <span className="font-bold text-slate-900 font-mono">{selectedSlipModal.orderId || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">รหัสธุรกรรม / Journal ID:</span>
                      <span className="font-bold text-teal-700 font-mono text-[11px]">{selectedSlipModal.transactionId || selectedSlipModal.journalId || '-'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ยอดเงินที่ชำระ:</span>
                      <span className="font-black text-emerald-700 font-mono text-sm">฿{Number(selectedSlipModal.amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ชื่อร้านค้า (Merchant):</span>
                      <span className="font-bold text-slate-900">{selectedSlipModal.merchantName || 'GIFFFORYOU.COM'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">รหัสสมาชิก (MID):</span>
                      <span className="font-bold text-indigo-700 font-mono">108037654</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ผู้ชำระเงิน:</span>
                      <span className="font-medium text-slate-800">{selectedSlipModal.payerName || '-'} ({selectedSlipModal.payerAccountMasked || ''})</span>
                    </div>
                    <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                      <span className="text-slate-500">ช่องทางชำระเงิน:</span>
                      <span className="font-medium text-slate-800">{selectedSlipModal.paymentChannel || 'QR รับเงิน'}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-slate-500">เวลาในสลิป:</span>
                      <span className="font-medium text-slate-700 text-[11px]">{selectedSlipModal.slipDate || '-'}</span>
                    </div>
                  </div>

                  {/* Verification Notes */}
                  {selectedSlipModal.verificationDetails?.validationNotes?.length > 0 && (
                    <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 space-y-1">
                      <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-emerald-600" />
                        <span>ผลการตรวจสอบสถาปัตยกรรม (Verification Report)</span>
                      </div>
                      <ul className="text-[11px] text-emerald-900 space-y-0.5 list-disc list-inside">
                        {selectedSlipModal.verificationDetails.validationNotes.map((note: string, idx: number) => (
                          <li key={idx}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="text-[10px] text-slate-400 font-mono truncate max-w-xs">
                SHA256: {selectedSlipModal.slipSha256 ? `${selectedSlipModal.slipSha256.substring(0, 16)}...` : '-'}
              </div>
              <button
                onClick={() => setSelectedSlipModal(null)}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
